import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Space, Tooltip, Button, PageHeader, Input, Avatar, Result } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  PlusCircleOutlined,
  TeamOutlined,
  UserOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  MyBreadcrumb,
  accessControls,
  confirmDelete,
} from 'utils';
import {
  usersSuperAdminListService,
  usersDeleteService,
} from 'services/user';
import Auth from 'auth';
import { legalAcceptanceColumns } from './legalAcceptanceColumns';
import { debounce } from 'lodash';

const { Search } = Input;

function formatUserRole(role) {
  switch (role) {
    case 10:
      return '10 - Super Admin';
    case 11:
      return '11 - Super Admin Assistant';
    case 12:
      return '12 - Workspace Managers';
    case 13:
      return '13 - Super Admin Assistant Tech Six';
    default:
      return `${role} - Custom Role`;
  }
}

export default function SuperAdminUsersList() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const isSuperAdmin = Auth.getRole() === 10;

  const columns = [
    {
      title: 'Photo',
      key: 'photo',
      render: (text, record) =>
        record.photo ? (
          <Avatar src={record.photo} />
        ) : (
          <Avatar icon={<UserOutlined />} />
        ),
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) =>
        String(a.display_name || '').localeCompare(String(b.display_name || '')),
    },
    {
      title: 'Reseller',
      dataIndex: 'client',
      key: 'client',
      render: (text, record) =>
        record.client?.client_name || '—',
      sorter: (a, b) =>
        String(a.client?.client_name || '').localeCompare(
          String(b.client?.client_name || ''),
        ),
    },
    {
      title: 'Role',
      dataIndex: 'roleLabel',
      key: 'role',
      sorter: (a, b) => Number(a.role) - Number(b.role),
    },
    {
      title: 'Email/Username',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => String(a.email || '').localeCompare(String(b.email || '')),
    },
    {
      title: 'Cell Phone',
      dataIndex: 'cell_phone',
      key: 'cell_phone',
    },
    {
      title: 'Third-Party Credentials',
      key: 'third-party-credentials',
      align: 'center',
      render: (text, record) => (
        <a href={`/third-party-credentials?uid=${record._id}`}>View</a>
      ),
    },
    ...legalAcceptanceColumns,
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          {accessControls.can(permissions, 'User.read:any') && (
            <Tooltip title="View">
              <Button
                onClick={() => history.push('/users/view/' + record._id)}
                type="primary"
                shape="circle"
                icon={<EyeOutlined />}
              />
            </Tooltip>
          )}
          {accessControls.can(permissions, 'User.update:any') && (
            <Tooltip title="Edit">
              <Button
                onClick={() => history.push('/users/update/' + record._id)}
                type="primary"
                shape="circle"
                icon={<EditOutlined />}
              />
            </Tooltip>
          )}
          {accessControls.can(permissions, 'User.update:any') && (
            <Tooltip title="Change Password">
              <Button
                onClick={() =>
                  history.push('/users/update-password/' + record._id)
                }
                shape="circle"
                icon={<LockOutlined />}
              />
            </Tooltip>
          )}
          {accessControls.can(permissions, 'User.delete:any') && (
            <Tooltip title="Disable">
              <Button
                onClick={() => deleteRecord(record._id)}
                shape="circle"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (isSuperAdmin) {
      loadData();
    }
    accessControls.fetch().then((nextPermissions) => {
      setPermissions(nextPermissions);
    });
  }, [isSuperAdmin]);

  const loadData = (q) => {
    setIsLoading(true);
    usersSuperAdminListService(q)
      .then((response) => {
        setIsLoading(false);
        const items = response.data.items || [];
        setDataSource(
          items.map((item) => ({
            ...item,
            roleLabel: formatUserRole(item.role),
          })),
        );
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      usersDeleteService(id)
        .then((response) => {
          setIsLoading(false);
          handleApiSuccess(response);
          loadData();
        })
        .catch((error) => {
          setIsLoading(false);
          handleApiError(error);
        });
    });
  };

  const delayedQuery = debounce((q) => loadData(q), 500);
  const onSearchChange = (e) => {
    delayedQuery(e.target.value);
  };

  if (!isSuperAdmin) {
    return (
      <Result
        status="403"
        title="Access denied"
        subTitle="Only Super Admin can manage Super Admin users."
      />
    );
  }

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'Super Admin', path: '/super-admin' },
          { label: 'Super Admin Users', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        avatar={{ icon: <TeamOutlined /> }}
        title="Super Admin Users"
        subTitle={
          <Search
            allowClear
            onChange={onSearchChange}
            loading={isLoading}
            placeholder="Filter search result"
            onSearch={(value) => loadData(value)}
            style={{ width: 220 }}
          />
        }
        extra={[
          accessControls.can(permissions, 'User.create:any') ? (
            <Button
              key="create"
              shape="round"
              onClick={() => history.push('/users/create')}
              type="primary"
              icon={<PlusCircleOutlined />}
            >
              Add New
            </Button>
          ) : null,
        ]}
      />
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={isLoading}
        rowKey="_id"
      />
    </div>
  );
}
